import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { vendorsApi, type VendorInput } from '../features/vendors/api.ts';
import { vendorTypesApi } from '../features/vendorTypes/api.ts';
import { ManageTypes } from '../components/ManageTypes.tsx';
import { useAuthStore } from '../store/auth.ts';
import { canProcure } from '../lib/roles.ts';
import { t } from '../lib/strings.ts';
import { formatDate } from '@/lib/format';
import { Pagination } from '../components/Pagination.tsx';

const PAGE_SIZE = 10;

type ActiveFilter = 'all' | 'active' | 'inactive';

type FieldErrors = Partial<Record<keyof VendorInput, string>>;

function parseServerError(err: unknown): FieldErrors {
  // axios error shape
  const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
  if (data?.error === 'invalid_id_card') {
    return { idCardNumber: t.vendors.errorIdCard };
  }
  return {};
}

export function Vendors(): JSX.Element {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const mayMutate = canProcure(role);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => { setDebounced(search.trim().toLowerCase()); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');

  // Page resets whenever a filter/search changes (the result set changes).
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [activeFilter, debounced]);

  const { data, isLoading } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => vendorsApi.list({ page: 1, pageSize: 1000 }),
  });

  const { data: types = [] } = useQuery({
    queryKey: ['vendor-types'],
    queryFn: () => vendorTypesApi.list(),
  });

  const allVendors = useMemo(() => data?.vendors ?? [], [data]);

  const filtered = useMemo(() => allVendors.filter((v) => {
    if (activeFilter === 'active' && !v.isActive) return false;
    if (activeFilter === 'inactive' && v.isActive) return false;
    if (debounced) {
      const hay = `${v.name} ${v.idCardNumber} ${v.phone ?? ''}`.toLowerCase();
      if (!hay.includes(debounced)) return false;
    }
    return true;
  }), [allVendors, activeFilter, debounced]);

  const total = filtered.length;
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const [tab, setTab] = useState<'vendors' | 'types'>('vendors');
  const [showAdd, setShowAdd] = useState(false);
  const emptyForm: VendorInput = { name: '', phone: '', idCardNumber: '', address: '', bankAccountNumber: '', bankAccountName: '', email: '', typeIds: [] };
  const [form, setForm] = useState<VendorInput>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});
    setSubmitError(null);
    setIsPending(true);
    try {
      await vendorsApi.create({
        name: form.name.trim(),
        phone: form.phone?.trim() || undefined,
        idCardNumber: form.idCardNumber.trim(),
        address: form.address?.trim() || undefined,
        bankAccountNumber: form.bankAccountNumber?.trim() || undefined,
        bankAccountName: form.bankAccountName?.trim() || undefined,
        email: form.email?.trim() || undefined,
        typeIds: form.typeIds,
      });
      setShowAdd(false);
      setForm(emptyForm);
      void qc.invalidateQueries({ queryKey: ['vendors'] });
    } catch (err) {
      const fe = parseServerError(err);
      if (Object.keys(fe).length > 0) {
        setFieldErrors(fe);
      } else {
        setSubmitError(t.vendors.errorSave);
      }
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="page space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="font-serif text-3xl tracking-tight">{t.vendors.title}</h1>
        {tab === 'vendors' && mayMutate && (
          <button
            onClick={() => { setShowAdd((v) => !v); setFieldErrors({}); setSubmitError(null); }}
            className="btn-primary"
          >
            {t.vendors.addVendor}
          </button>
        )}
      </header>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        <button
          type="button"
          onClick={() => setTab('vendors')}
          className={['-mb-px border-b-2 px-4 py-2 text-sm font-medium', tab === 'vendors' ? 'border-primary text-ink' : 'border-transparent text-muted-foreground hover:text-foreground'].join(' ')}
        >
          {t.vendors.tabVendors}
        </button>
        <button
          type="button"
          onClick={() => setTab('types')}
          className={['-mb-px border-b-2 px-4 py-2 text-sm font-medium', tab === 'types' ? 'border-primary text-ink' : 'border-transparent text-muted-foreground hover:text-foreground'].join(' ')}
        >
          {t.vendors.tabTypes}
        </button>
      </div>

      {tab === 'types' && <ManageTypes />}

      {tab === 'vendors' && (
      <>
      {showAdd && mayMutate && (
        <section className="card-paper space-y-4 p-5">
          <h2 className="status-label text-muted-foreground">{t.vendors.newVendorHeading}</h2>
          <form className="space-y-4" onSubmit={(e) => { void handleCreate(e); }}>
            {/* Name */}
            <div className="space-y-1">
              <label className="field-label">{t.vendors.fieldName} <span className="text-destructive">{t.vendors.fieldRequired}</span></label>
              <input
                required
                className="input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {/* Phone */}
              <div className="space-y-1">
                <label className="field-label">{t.vendors.fieldPhone}</label>
                <input
                  className="input num"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </div>

              {/* ID Card */}
              <div className="space-y-1">
                <label className="field-label">
                  {t.vendors.fieldIdCard} <span className="text-destructive">{t.vendors.fieldRequired}</span>
                  <span className="ms-1 text-xs font-normal text-muted-foreground">{t.vendors.fieldIdCardFormat}</span>
                </label>
                <input
                  required
                  className={['input num', fieldErrors.idCardNumber ? 'border-destructive' : ''].join(' ')}
                  placeholder={t.vendors.fieldIdCardPlaceholder}
                  value={form.idCardNumber}
                  onChange={(e) => setForm((f) => ({ ...f, idCardNumber: e.target.value }))}
                />
                {fieldErrors.idCardNumber && (
                  <p className="text-xs text-destructive" role="alert">{fieldErrors.idCardNumber}</p>
                )}
              </div>
            </div>

            {/* Address */}
            <div className="space-y-1">
              <label className="field-label">{t.vendors.fieldAddress}</label>
              <input
                className="input"
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {/* Bank Account Number */}
              <div className="space-y-1">
                <label className="field-label">{t.vendors.fieldBankAccountNumber}</label>
                <input
                  className="input num"
                  value={form.bankAccountNumber}
                  onChange={(e) => setForm((f) => ({ ...f, bankAccountNumber: e.target.value }))}
                />
              </div>

              {/* Bank Account Name */}
              <div className="space-y-1">
                <label className="field-label">{t.vendors.fieldBankAccountName}</label>
                <input
                  className="input"
                  value={form.bankAccountName}
                  onChange={(e) => setForm((f) => ({ ...f, bankAccountName: e.target.value }))}
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1">
              <label className="field-label">{t.vendors.fieldEmail}</label>
              <input
                type="email"
                className="input"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />            </div>

            {/* Vendor Types */}
            {types.length > 0 && (
              <div className="space-y-1">
                <label className="field-label">{t.vendors.fieldVendorTypes}</label>
                <div className="flex flex-wrap gap-2">
                  {types.map((vt) => (
                    <button
                      key={vt.id}
                      type="button"
                      onClick={() => setForm((f) => {
                        const ids = f.typeIds ?? [];
                        return { ...f, typeIds: ids.includes(vt.id) ? ids.filter((id) => id !== vt.id) : [...ids, vt.id] };
                      })}
                      className={form.typeIds?.includes(vt.id) ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
                    >
                      {vt.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {submitError && (
              <p className="text-sm text-destructive" role="alert">{submitError}</p>
            )}

            <div className="flex gap-2">
              <button type="submit" disabled={isPending} className="btn-primary">
                {isPending ? t.vendors.saving : t.vendors.saveVendor}
              </button>
              <button type="button" onClick={() => setShowAdd(false)} className="btn-ghost">
                {t.vendors.cancel}
              </button>
            </div>
          </form>
        </section>
      )}

      {/* Toolbar — M3 segmented active/inactive filter on one side, search on the
          other, on the tinted chrome ground like WapMeet's meetings list. */}
      <div className="rounded-3xl bg-[hsl(var(--chrome))] flex flex-wrap items-center justify-between gap-x-6 gap-y-3 p-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="inline-flex items-center overflow-hidden rounded-full border border-[hsl(var(--border))]"
            role="group"
            aria-label={t.vendors.title}
          >
            {([
              { key: 'all', label: t.vendors.filterAll },
              { key: 'active', label: t.vendors.statusActive },
              { key: 'inactive', label: t.vendors.statusInactive },
            ] as { key: ActiveFilter; label: string }[]).map((tab, i) => {
              const active = activeFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setActiveFilter(tab.key)}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium transition-colors ${
                    i > 0 ? 'border-s border-[hsl(var(--border))]' : ''
                  } ${
                    active
                      ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
                      : 'text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                  }`}
                >
                  {active && <Icon name="check" size={16} />}
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="relative w-full sm:w-72">
          <Icon name="search" size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.vendors.searchPlaceholder}
            aria-label={t.vendors.searchAriaLabel}
            className="input rounded-full py-1.5 ps-9 pe-4"
          />
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">{t.vendors.loading}</p>
      ) : (
        <section className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
          {pageItems.map((v) => (
            <Link
              key={v.id}
              to="/vendors/$vendorId"
              params={{ vendorId: v.id }}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-4 transition-colors hover:bg-[hsl(var(--surface-high))] sm:px-5"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium leading-snug">
                  <span>{v.name}</span>
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="credit_card" size={15} className="shrink-0 text-foreground/40" />
                    <span className="num">{v.idCardNumber}</span>
                  </span>
                  {v.phone && (
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="call" size={15} className="shrink-0 text-foreground/40" />
                      <span className="num">{v.phone}</span>
                    </span>
                  )}
                  {v.types && v.types.length > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="sell" size={15} className="shrink-0 text-foreground/40" />
                      {v.types.map((vt) => vt.name).join('، ')}
                    </span>
                  )}
                  {mayMutate && (
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="key" size={15} className="shrink-0 text-foreground/40" />
                      {!v.email
                        ? t.vendors.credentialsNoEmail
                        : v.credentialsSentAt
                          ? <span className="num">{formatDate(v.credentialsSentAt)}</span>
                          : t.vendors.credentialsNotSent}
                    </span>
                  )}
                </div>
              </div>
              <span className={['pill shrink-0', v.isActive ? 'pill-active' : 'pill-neutral'].join(' ')}>
                {v.isActive ? t.vendors.statusActive : t.vendors.statusInactive}
              </span>
            </Link>
          ))}
          {total === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">
              {debounced ? t.vendors.emptySearch : t.vendors.emptyList}
            </p>
          )}
        </section>
      )}

      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
      </>
      )}
    </div>
  );
}
